import { en } from './locales/en'
import { zh } from './locales/zh'
import { zhTW } from './locales/zh-TW'
import { ja } from './locales/ja'
import { ko } from './locales/ko'
import { es } from './locales/es'
import { fr } from './locales/fr'
import { de } from './locales/de'
import type { StringKey, StringTable } from './types'

export type { StringKey, StringTable } from './types'
export { en } from './locales/en'

const locales: Record<string, StringTable> = { en, zh, 'zh-TW': zhTW, ja, ko, es, fr, de }

/**
 * Map a LanguageProfile `nativeLang` to its UI locale-table key.
 *
 * The en-zh-hant pair's native id is the BCP-47 *script* tag `zh-hant`
 * (covers TW/HK/Macau/overseas 繁體 — plan en-zh-hant.md §2), but its chrome
 * strings live in the `zh-TW` table. Before this mapping, `getStrings('zh-hant')`
 * silently fell back to English — the whole app UI degraded to English for
 * Traditional Chinese users (user report 2026-08-22). Any future pair whose
 * native id ≠ a table key must be added here; guarded by i18n.test.ts.
 */
const NATIVE_TO_UI_LOCALE: Record<string, string> = {
  'zh-hant': 'zh-TW',
  // Canonical BCP-47 casing, in case a caller passes a locale tag verbatim.
  'zh-Hant': 'zh-TW',
}

/** Resolve the UI locale for a profile nativeLang / locale-ish string. */
export function uiLocaleFor(nativeLang: string): string {
  return NATIVE_TO_UI_LOCALE[nativeLang] ?? nativeLang
}

/** Get string table for a locale. Falls back to English for unknown locales. */
export function getStrings(locale: string): StringTable {
  return locales[uiLocaleFor(locale)] ?? locales.en
}

/** Interpolate {{var}} placeholders in a template string. */
export function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return Object.hasOwn(vars, key) ? String(vars[key]) : match
  })
}

/** Locale-table lookup with an English fallback. A locale file drifting out
 *  of sync (missing key) must degrade to English text, never surface
 *  `undefined` to interpolate() — that crashed the whole app with
 *  "Cannot read properties of undefined (reading 'replace')". */
export function lookupString(strings: StringTable, key: StringKey): string {
  return strings[key] ?? en[key] ?? key
}

/**
 * Resolve a plural variant of a key based on count.
 *
 * The locale data encodes plural pairs as `key_one` / `key_other`. Callers
 * pass the `_other` form (the default) plus a `count`; this picks `_one`
 * when `count === 1` and the singular variant exists, otherwise keeps `_other`.
 * Passing `_one` with `count !== 1` swaps back to `_other`. Non-plural keys
 * and keys whose counterpart is missing are returned unchanged.
 */
export function resolvePluralKey(
  strings: StringTable,
  key: string,
  count: number
): string {
  if (key.endsWith('_other')) {
    const one = `${key.slice(0, -'_other'.length)}_one`
    return count === 1 && Object.hasOwn(strings, one) ? one : key
  }
  if (key.endsWith('_one')) {
    const other = `${key.slice(0, -'_one'.length)}_other`
    return count !== 1 && Object.hasOwn(strings, other) ? other : key
  }
  return key
}
